'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { writeChartMetricCookie } from '@/lib/chart/metric-preference';
import { writeChartTimeframeCookie } from '@/lib/chart/timeframe-preference';
import type { ChartInterval, ChartMode, RenderMode, YAxisProperty } from '@/lib/chart/types';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

// ---------------------------------------------------------------------------
// Toggle-able boolean setting keys
// ---------------------------------------------------------------------------

type ToggleKey =
  | 'showBoosts'
  | 'showTrades'
  | 'showBreakEven'
  | 'legendVisible'
  | 'showXAxis'
  | 'showYAxis'
  | 'showPriceLines'
  | 'alwaysShowTooltip'
  | 'scaleMarketShareOnHover';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/** One-tap card-chip trade progress for a market. The short resolved phase lets
 *  the position card transition from Buying/Selling to Bought/Sold (or its
 *  failure state) before restoring the rail. `null` when idle. */
export interface AutoTradePending {
  outcomeIndex: number;
  direction: 'buy' | 'sell';
  buyUsdc?: number;
  sellPercent?: number;
  max?: boolean;
  phase?: 'pending' | 'success' | 'error';
}

interface ChartSettingsState {
  // Mode & rendering
  chartMode: ChartMode;
  interval: ChartInterval;
  yAxisProperty: YAxisProperty;
  renderMode: RenderMode;

  // Boolean toggles
  showBoosts: boolean;
  showTrades: boolean;
  // Per-outcome break-even avg-entry reference lines on the chart (default on);
  // each line is suppressed when its outcome is hidden from the chart.
  showBreakEven: boolean;
  showXAxis: boolean;
  showYAxis: boolean;
  showPriceLines: boolean;
  // Pin the per-series crosshair tooltip to the latest point without hovering
  // (the time pill stays hidden in this pinned state).
  alwaysShowTooltip: boolean;
  // Market Share only: while hovering (or holding the touch tooltip) the Y axis
  // eases out until the resolution target sits at the top, then eases back.
  scaleMarketShareOnHover: boolean;

  // Layout
  yAxisSide: 'left' | 'right';
  legendVisible: boolean;

  // Per-market outcome visibility
  hiddenOutcomes: Record<string, number[]>;
  outcomeFilters: Record<string, number[]>;

  // Per-market selected outcome for trade form pre-selection
  selectedOutcome: Record<string, number | null>;

  // One-shot trade direction request — when nonce increments, the trade form
  // syncs its local direction to `direction` and, for `amount: 'max'`, prefills
  // the amount field with the full sellable position. Used by the Holdings row
  // click and the portfolio sell deep-link to open the form for MANUAL review.
  // (The position card's quick-trade chips no longer use this — they execute
  // self-contained through the shared trade executor.)
  tradeDirectionRequest: Record<
    string,
    {
      direction: 'buy' | 'sell';
      amount?: 'max';
      nonce: number;
    }
  >;

  // One-tap trade progress per market. This is deliberately excluded from the
  // persisted slice below so a reload can never revive stale interaction state.
  autoTradePending: Record<string, AutoTradePending | null>;

  // Actions
  setChartMode: (mode: ChartMode) => void;
  setInterval: (interval: ChartInterval) => void;
  setYAxisProperty: (property: YAxisProperty) => void;
  setRenderMode: (mode: RenderMode) => void;
  toggleSetting: (key: ToggleKey) => void;
  toggleOutcome: (market: string, outcomeIndex: number) => void;
  setHiddenOutcomes: (market: string, indices: number[]) => void;
  setSelectedOutcome: (market: string, index: number | null) => void;
  toggleOutcomeFilter: (market: string, index: number, outcomeIndices: readonly number[]) => void;
  requestTradeDirection: (
    market: string,
    direction: 'buy' | 'sell',
    options?: { amount?: 'max' },
  ) => void;
  setAutoTradePending: (market: string, pending: AutoTradePending | null) => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useChartSettingsStore: UseBoundStore<StoreApi<ChartSettingsState>> =
  createTrackedStore<ChartSettingsState>('useChartSettingsStore')(
    persist(
      (set, get) => ({
        // Defaults
        chartMode: 'consensus',
        interval: 'live',
        yAxisProperty: 'marketCap',
        renderMode: 'line',

        showBoosts: false,
        showTrades: true,
        showBreakEven: true,
        showXAxis: true,
        showYAxis: true,
        showPriceLines: false,
        alwaysShowTooltip: false,
        scaleMarketShareOnHover: true,

        yAxisSide: 'right',
        legendVisible: true,

        hiddenOutcomes: {},
        outcomeFilters: {},
        selectedOutcome: {},
        tradeDirectionRequest: {},
        autoTradePending: {},

        // Actions
        setChartMode: (mode: ChartMode): void => {
          set({ chartMode: mode });
          writeChartMetricCookie(get());
        },

        setInterval: (interval: ChartInterval): void => {
          set({ interval });
          writeChartTimeframeCookie(interval);
        },

        setYAxisProperty: (property: YAxisProperty): void => {
          set({ yAxisProperty: property });
          writeChartMetricCookie(get());
        },

        setRenderMode: (mode: RenderMode): void => {
          set({ renderMode: mode });
        },

        toggleSetting: (key: ToggleKey): void => {
          set((state) => ({ [key]: !state[key] }));
        },

        toggleOutcome: (market: string, outcomeIndex: number): void => {
          set((state) => {
            const current = state.hiddenOutcomes[market] ?? [];
            const idx = current.indexOf(outcomeIndex);
            const next =
              idx === -1 ? [...current, outcomeIndex] : current.filter((i) => i !== outcomeIndex);
            return {
              hiddenOutcomes: { ...state.hiddenOutcomes, [market]: next },
            };
          });
        },

        setHiddenOutcomes: (market: string, indices: number[]): void => {
          set((state) => ({
            hiddenOutcomes: { ...state.hiddenOutcomes, [market]: indices },
            outcomeFilters: { ...state.outcomeFilters, [market]: [] },
          }));
        },

        setSelectedOutcome: (market: string, index: number | null): void => {
          set((state) => ({
            selectedOutcome: { ...state.selectedOutcome, [market]: index },
          }));
        },

        toggleOutcomeFilter: (
          market: string,
          index: number,
          outcomeIndices: readonly number[],
        ): void => {
          set((state) => {
            const selected = new Set(state.outcomeFilters[market]);
            if (selected.has(index)) selected.delete(index);
            else selected.add(index);
            return {
              hiddenOutcomes: {
                ...state.hiddenOutcomes,
                [market]:
                  selected.size === 0
                    ? []
                    : outcomeIndices.filter((candidate) => !selected.has(candidate)),
              },
              outcomeFilters: { ...state.outcomeFilters, [market]: [...selected] },
              selectedOutcome: { ...state.selectedOutcome, [market]: index },
            };
          });
        },

        requestTradeDirection: (
          market: string,
          direction: 'buy' | 'sell',
          options?: { amount?: 'max' },
        ): void => {
          set((state) => {
            const prev = state.tradeDirectionRequest[market];
            const nextNonce = (prev?.nonce ?? 0) + 1;
            const next: { direction: 'buy' | 'sell'; amount?: 'max'; nonce: number } = {
              direction,
              nonce: nextNonce,
            };
            if (options?.amount === 'max') {
              next.amount = 'max';
            }
            return {
              tradeDirectionRequest: {
                ...state.tradeDirectionRequest,
                [market]: next,
              },
            };
          });
        },

        setAutoTradePending: (market: string, pending: AutoTradePending | null): void => {
          set((state) => ({
            autoTradePending: { ...state.autoTradePending, [market]: pending },
          }));
        },
      }),
      {
        name: 'morfi-chart-settings',
        version: 10,
        // ALLOW-UNKNOWN: zustand persist migration API requires unknown for persisted state
        migrate: (persisted: unknown, version: number): ChartSettingsState => {
          const state = persisted as ChartSettingsState;
          if (version === 0) {
            // v0 → v1: default interval changed from '1h' to 'all'
            state.interval = 'all';
          }
          if (version < 3) {
            // v2 → v3: settings cleanup. Removed the endpoint label/value
            // toggles (`showOutcomeLabel` / `showLiveValue`) and the two
            // non-functional reference-line toggles (`showBreakEven` /
            // `showGraduation` — never wired to the chart). Added the
            // pinned-tooltip toggle (default off). Stale keys are simply no
            // longer read or persisted (partialize drops them on next write).
            state.alwaysShowTooltip = false;
          }
          if (version < 4) {
            // v3 → v4: price lines became off-by-default. Reset persisted
            // prefs to the new baseline so the default change takes effect
            // even for users already migrated to v3 earlier.
            state.showPriceLines = false;
          }
          if (version < 5) {
            // v4 → v5: the timeframe selector changed from bucket sizes to
            // lookback windows. Remap persisted bucket values to the window
            // with the nearest effective lookback, so existing users keep a
            // sensible view and stale buckets aren't persisted as invalid
            // timeframes. (Old `1h`/`1d` were buckets whose hidden windows were
            // 7 d / 180 d — hence `1w` / `all`, NOT the same-named windows.)
            // The persisted value may be an old bucket string no longer in the
            // ChartInterval union; `ChartInterval` is string-assignable, so the
            // record lookup is well-typed without a cast.
            const remap: Record<string, ChartInterval> = {
              '1m': '1h',
              '5m': '6h',
              '15m': '1d',
              '1h': '1w',
              '4h': '1mo',
              '1d': 'all',
            };
            const mapped = remap[state.interval];
            if (mapped !== undefined) state.interval = mapped;
          }
          if (version < 6) {
            // v5 → v6: break-even moved from a per-outcome opt-in
            // (`breakEvenOutcome`) to a single global toggle, on by default. The
            // stale `breakEvenOutcome` key is no longer read or persisted.
            state.showBreakEven = true;
          }
          if (version < 7) {
            // The market-detail landing view is live Market Share. Reset the
            // persisted chart selection so prior Price/Candle preferences do
            // not become the first view when opening a market.
            state.chartMode = 'consensus';
            state.interval = 'live';
            state.renderMode = 'line';
          }
          if (version < 8) {
            // Keep the latest-value pills visible when a rightward touch scrub
            // ends instead of reverting to the old fade-out behavior.
            state.alwaysShowTooltip = true;
          }
          if (version < 9) {
            // Default pinned tooltips to off on both market and winner charts.
            state.alwaysShowTooltip = false;
          }
          if (version < 10) {
            // Old visibility preferences do not distinguish all selected from no filter.
            state.hiddenOutcomes = {};
            state.outcomeFilters = {};
          }
          return state;
        },
        // Persist only serializable state — actions are re-created by Zustand.
        // Sets are not in persisted state so no custom serialization needed.
        partialize: (
          state: ChartSettingsState,
        ): Omit<
          ChartSettingsState,
          | 'setChartMode'
          | 'setInterval'
          | 'setYAxisProperty'
          | 'setRenderMode'
          | 'toggleSetting'
          | 'toggleOutcome'
          | 'setHiddenOutcomes'
          | 'setSelectedOutcome'
          | 'toggleOutcomeFilter'
          | 'requestTradeDirection'
          | 'tradeDirectionRequest'
          | 'setAutoTradePending'
          | 'autoTradePending'
        > => ({
          chartMode: state.chartMode,
          interval: state.interval,
          yAxisProperty: state.yAxisProperty,
          renderMode: state.renderMode,
          showBoosts: state.showBoosts,
          showTrades: state.showTrades,
          showBreakEven: state.showBreakEven,
          showXAxis: state.showXAxis,
          showYAxis: state.showYAxis,
          showPriceLines: state.showPriceLines,
          alwaysShowTooltip: state.alwaysShowTooltip,
          scaleMarketShareOnHover: state.scaleMarketShareOnHover,
          yAxisSide: state.yAxisSide,
          legendVisible: state.legendVisible,
          hiddenOutcomes: state.hiddenOutcomes,
          outcomeFilters: state.outcomeFilters,
          selectedOutcome: state.selectedOutcome,
        }),
      },
    ),
  );
